import React, { useState } from 'react';
import { Box, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CustomerSelector from '../Common/CustomerSelector';
import SiteSelector from '../Common/SiteSelector';
import SchoolSelector from '../Common/Selectors/SchoolSelector';
import { CourseDeliveryDTO, SiteDTO, Product, ProductDTO, CustomerDTO, CourseSetDTO, TestAheadCourseDTO } from '@beable/roster-backend-client-v1';
import { useCourseState, useCourseDispatch, CourseActionType, ProvisionCourseStep } from '../../context/CourseContext';
import ProvisionTypeSelector from '../Common/Selectors/ProvisionTypeSelector';
import ProductSelectList from '../Common/Selectors/ProductSelectList';
import TestAheadCourseSelector from '../Common/Selectors/TestAheadCourseSelector';
import courseService from '../../services/CourseService';
import { StyledPanelHeader } from '../Common/Layout/PanelHeader';
import BasePanel from '../Common/Layout/BasePanel';
import { getStyles } from './styles';

const ProvisionCoursesPanelBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.fade,
  flex: 1,
}));

export default function ProvisionCoursesPanel() {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { activeStep, site, schoolId, course, courseSet, testAheadCourse, product } = useCourseState();
  const courseDispatch = useCourseDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | undefined>(undefined);

  const handleCustomerChange = (customer: CustomerDTO) => {
    setSelectedCustomer(customer);
    courseDispatch({
      type: CourseActionType.CustomerChanged,
      customer: customer,
    });
  };

  const handleCourseSetChange = async (courseSet: CourseSetDTO) => {
    const fullCourseSet = await courseService.getCourseSet(courseSet?.id ?? '');
    courseDispatch({
      type: CourseActionType.CourseSetChanged,
      courseSet: fullCourseSet ?? courseSet,
    })
  };

  const isDisabled = () => {
    return activeStep !== ProvisionCourseStep.Access;
  }

  return (
    <BasePanel>
      <ProvisionCoursesPanelBox>
        <StyledPanelHeader header='Provision Courses' />
        <Box sx={styles.provisionPanelContent}>
          <CustomerSelector
            onSelect={handleCustomerChange}
            disabled={isDisabled()}
            variant='outlined'
            inPanel
          />

          <SiteSelector
            siteId={site?.id}
            customerId={selectedCustomer ? selectedCustomer.id : ''}
            onSelect={(site: SiteDTO) => {
              courseDispatch({
                type: CourseActionType.SiteChanged,
                site
              })
            }}
            disabled={isDisabled()}
            variant='outlined'
            inPanel
          />

          <SchoolSelector
            siteId={site ? site.id : 0}
            schoolId={schoolId}
            onChange={(schoolId: string) => {
              courseDispatch({
                type: CourseActionType.SchoolChanged,
                schoolId
              })
            }}
            allOption={true}
            disabled={isDisabled()}
            variant='outlined'
            inPanel
          />

          <ProductSelectList
            productId={product?.id ?? ''}
            customerId={selectedCustomer?.id ?? ''}
            siteId={site?.id ?? 0}
            onChange={(product: ProductDTO) => {
              courseDispatch({
                type: CourseActionType.ProductChanged,
                product
              })
            }}
            disabled={isDisabled()}
            variant='outlined'
            inPanel
          />

          { product?.code === Product.Literacy &&
            <ProvisionTypeSelector
              course={course}
              courseSet={courseSet}
              locationCode={selectedCustomer?.locationCode}
              customerId={selectedCustomer?.id}
              siteId={site?.id}
              onCourseChange={(course: CourseDeliveryDTO) => {
                courseDispatch({
                  type: CourseActionType.CourseChanged,
                  course
                })
              }}
              onCourseSetChange={handleCourseSetChange}
              disabled={isDisabled()}
              inPanel
            />
          }

          { product?.code === Product.Exampal &&
            <TestAheadCourseSelector
              courseId={testAheadCourse?.id}
              onChange={(testAheadCourse: TestAheadCourseDTO) => {
                courseDispatch({
                  type: CourseActionType.TestAheadCourseChanged,
                  testAheadCourse
                })
              }}
              disabled={isDisabled()}
              variant='outlined'
              inPanel
            />
          }
        </Box>
      </ProvisionCoursesPanelBox>
    </BasePanel>
  );
}
